// @flow
import * as React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { isLocationEmpty } from 'utils/search/sidefilters';
import { trackClicked } from 'utils/analytics';
import SearchFiltersSidebar from 'components/SearchFiltersSidebar';
import Box from 'components/Box';
import ToggleInput from 'components/ToggleInput';
import { BodySmall } from 'components/Text';
import LocationInputContainer from 'containers/LocationInputContainer';
import SearchRadiusSelector from 'components/SearchRadiusSelector';
import {
  areFiltersDefault,
  sideFacetsForType,
  searchableFacetsForType
} from 'utils/search/searchFacets';
import type { Dispatch } from '../redux/main';
import {
  updateSearchLocation,
  clearSearchLocation
} from '../redux/main/actions/currentUser';
import {
  setSearchFilter as setSearchFilterAction,
  selectSearchFilterOption as selectSearchFilterOptionAction,
  deselectSearchFilterOption as deselectSearchFilterOptionAction,
  selectAllSearchFilterOptions,
  setSearchSort,
  setSearchRadius as setSearchRadiusAction,
  clearAllFilters,
  setUseLocation as setUseLocationAction
} from '../redux/main/actions/mainSearch';
import type { SortType } from '../redux/main/reducers/mainSearch';
import { colors, gutterWidth } from '../theme';

const ToggleFilter = ({
  onChange,
  name,
  value,
  title,
  children,
  ...props
}: {
  onChange: Function,
  name: string,
  value: boolean,
  title: React.Node,
  children?: React.Node,
  'data-qa-id': string
}) => (
  <Box
    py={17}
    px={[gutterWidth / 2, null, null, 0]}
    borderTop={`1px solid ${colors.aluminum}`}
  >
    <Box display="flex" justifyContent="space-between" alignItems="center">
      <label htmlFor={name}>
        <BodySmall>{title}</BodySmall>
      </label>
      <ToggleInput
        data-qa-id={props['data-qa-id']}
        id={name}
        value={value}
        onChange={onChange}
      />
    </Box>
    {children}
  </Box>
);

const mapStateToProps = ({
  currentUser: { searchLocation },
  searchRadius,
  mainSearch: {
    query,
    sort,
    filters,
    searchFacets,
    facetsThatArentLoaded,
    useLocation
  }
}) => ({
  searchLocation,
  searchRadius: searchRadius.value,
  sort: sort[filters.type],
  filters,
  searchFacets,
  facetsThatArentLoaded,
  query,
  useLocation
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
  ...bindActionCreators(
    {
      setSearchFilter: setSearchFilterAction,
      selectSearchFilterOption: selectSearchFilterOptionAction,
      deselectSearchFilterOption: deselectSearchFilterOptionAction,
      selectAllSearchFilterOptions,
      setSearchRadius: setSearchRadiusAction,
      setSearchSort,
      clearAllFilters,
      setUseLocation: setUseLocationAction
    },
    dispatch
  ),
  setSearchLocation: (searchLocation: ?SearchLocation) => {
    if (searchLocation) {
      dispatch(updateSearchLocation(searchLocation, true));
    } else {
      dispatch(clearSearchLocation(true));
    }
  },
  setSearchRadius: (searchRadius: string) => {
    dispatch(setSearchRadiusAction(searchRadius, true));
  }
});

type Props = {
  query: string,
  searchLocation: ?SearchLocation,
  searchRadius: string,
  filters: SearchFilters,
  searchFacets: Object,
  setSearchFilter: Function,
  selectSearchFilterOption: Function,
  deselectSearchFilterOption: Function,
  selectAllSearchFilterOptions: Function,
  setSearchLocation: Function,
  setSearchRadius: Function,
  sort: SortType,
  setSearchSort: Function,
  clearAllFilters: Function,
  facetsThatArentLoaded: Array<string>,
  useLocation: boolean,
  setUseLocation: Function
};

const orderedFacets = [
  'isVirtual',
  'areasOfFocus',
  'actionType',
  'source',
  'actionDate',
  'functions',
  'locale'
];
const facetsToPassDown = [
  'areasOfFocus',
  'actionType',
  'source',
  'actionDate',
  'functions',
  'locale'
];

const VolopsSearchFiltersSidebarContainer = ({
  filters,
  searchFacets,
  facetsThatArentLoaded,
  setSearchLocation,
  searchLocation,
  searchRadius,
  setSearchFilter,
  setSearchRadius,
  useLocation,
  setUseLocation,
  selectSearchFilterOption,
  deselectSearchFilterOption,
  sort,
  ...otherProps
}: Props) => {
  const facets = filters.type
    ? sideFacetsForType(searchFacets, filters.type)
    : [];
  const filteredFacets = facets.filter(facet => {
    if (!facet.lazy) return true;
    return !facetsThatArentLoaded.includes(facet.name);
  });
  const facetsToRenderAsSelected = filteredFacets.sort(
    (a, b) => orderedFacets.indexOf(a.name) - orderedFacets.indexOf(b.name)
  );
  const facetsToRender = facetsToRenderAsSelected.filter(
    facet => facetsToPassDown.indexOf(facet.name) > -1
  );
  const searchLocationActive = useLocation && !isLocationEmpty(searchLocation);
  const defaultFiltersSet =
    areFiltersDefault(
      sideFacetsForType(searchFacets, 'VOLOP'),
      facetsThatArentLoaded,
      filters
    ) && !searchLocationActive;

  const toggleLocation = () => {
    trackClicked('Toggle Add Location', {
      context: 'Search Results',
      use_location: !useLocation
    });
    setUseLocation(!useLocation);
  };

  const toggleIsVirtual = () => {
    setSearchFilter(
      'isVirtualFacet',
      filters && filters.isVirtualFacet && filters.isVirtualFacet === 'YES'
        ? 'ALL'
        : 'YES'
    );
  };

  const toggleIsCovid = () => {
    setSearchFilter(
      'isCovidFacet',
      filters && filters.isCovidFacet && filters.isCovidFacet === 'YES'
        ? 'ALL'
        : 'YES'
    );
  };

  return (
    <SearchFiltersSidebar
      pb={[0, null, null, 64]}
      filters={filters}
      facetsToRender={facetsToRender}
      facetsToRenderAsSelected={facetsToRenderAsSelected}
      selectSearchFilterOption={selectSearchFilterOption}
      setSearchFilter={setSearchFilter}
      deselectSearchFilterOption={deselectSearchFilterOption}
      searchLocation={searchLocation}
      setSearchLocation={setSearchLocation}
      searchLocationActive={searchLocationActive}
      hideSearchLocation
      setSearchSort={setSearchSort}
      header={
        <Box>
          <ToggleFilter
            title={
              /* TRANSLATORS - Placeholder text for Location Field */ getText(
                'Use Location'
              )
            }
            onChange={toggleLocation}
            data-qa-id="toggle-has-location"
            name="location"
            value={useLocation}
          >
            {useLocation && (
              <Box data-qa-id="distance-section" mt={24}>
                <LocationInputContainer
                  onChange={setSearchLocation}
                  placeholder={getText('Try Montreal')}
                  locationText={searchLocation && searchLocation.text}
                  showLocationIcon
                  showClearButton
                />
                <Box mt="8px">
                  <SearchRadiusSelector
                    radius={searchRadius}
                    setSearchRadius={setSearchRadius}
                  />
                </Box>
              </Box>
            )}
          </ToggleFilter>
          <ToggleFilter
            data-qa-id="toggle-is-virtual"
            title={getText('Virtual / Remote Only')}
            value={
              !!(
                filters &&
                filters.isVirtualFacet &&
                filters.isVirtualFacet === 'YES'
              )
            }
            name="isVirtual"
            onChange={toggleIsVirtual}
          />
          <ToggleFilter
            data-qa-id="toggle-is-covid"
            title={getText('COVID-19 Only')}
            value={
              !!(
                filters &&
                filters.isCovidFacet &&
                filters.isCovidFacet === 'YES'
              )
            }
            name="isCovid"
            onChange={toggleIsCovid}
          />
        </Box>
      }
      searchableFacets={searchableFacetsForType('VOLOP')}
      sort={sort}
      defaultFiltersSet={defaultFiltersSet}
      {...otherProps}
    />
  );
};
export default connect<Props, {||}, _, _, _, _>(
  mapStateToProps,
  mapDispatchToProps
)(VolopsSearchFiltersSidebarContainer);
